import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from 'react';
import { Button, ButtonGroup, Col, Dropdown, FormLabel, Row } from 'react-bootstrap';
import FilterAccordion from "../../Common/Filter/FilterAccordion";
import InputDate from "../../Forms/Element/InputDate";
import { errorAlert, warnAlert, successAlert } from "../../alert/defaultAlert";
import { SearchOperation } from "../../../../utils/ApiUtils";
import axiosInstance from "../../../../services/AxiosInstance";
import Api from "../../../../constants/Api";
import GridCustomBase from "../../GridCustom/GridCustomBase";
import { formatDateTime, formatDateToYYYYMMDD } from "../../../../utils/DateUtils";
import Common from "../../../../constants/Common";
import InputText from "../../Forms/Element/InputText";
import { useNavigate } from "react-router-dom";
import MenuConfig from "../../../../constants/MenuConfig";
import { useBranch } from "../../../../context/BranchContext";
import { ApiError } from "../../Common/ApiError";
import GridCustomCheckBoxRow from "../../GridCustom/GridCustomCheckBoxRow";
import SaveIcon from "../../Icon/SaveIcon";
import AppointmentStatusSelect from "./AppointmentStatusSelect";
import AppointmentCurrentStatus from "./AppointmentCurrentStatus";
import AppointmentUpdateStatus from "./AppointmentUpdateStatus";
import { AppointmentDepositBadge } from "./AppointmentDeposit";
import Whatsapp from "../Tools/Whatsapp";
import AppointmentStatus from "./AppointmentStatus";
import { formatNumberWithTwoDecimals } from "../../../../utils/FormatUtils";
import UpdateStatusIcon from "../../Icon/UpdateStatusIcon";
import ModalPage from "../../Common/Modal/ModalPage";
import BranchSelect from "../Main/Branch/BranchSelect";
import ClubCrownIcon from "../../Icon/ClubCrownIcon";
import ClubLateIcon from "../../Icon/ClubLateIcon";
import "./AppointmentHistory.css";
import AppointmentDetailsModal from "./AppointmentDetailsModal";

const AppointmentHistory = () => {

    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [customer, setCustomer] = useState('');
    const [type, setType] = useState(null);
    const [employee, setEmployee] = useState('');
    const [branchFilter, setBranchFilter] = useState(null);
    const [showLoading, setShowLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const navigate = useNavigate();

    // Grid
    const [pageable, setPageable] = useState(null);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState([]);
    // Situação da reserva por id de agendamento, carregada em lote depois da página.
    const [depositSituations, setDepositSituations] = useState({});
    const [size, setSize] = useState(Common.DEFAULT_ROWS_PER_PAGE);

    const getCustomerName = useCallback((row) => row.customer?.fullName || '-', []);
    const getEmployeeName = useCallback((row) => row.employee?.fullName || '-', []);
    const getServiceName = useCallback((row) => row.service?.name || '-', []);
    const getBranchName = useCallback((row) => row.branch?.name || '-', []);
    const getTrimmedNotes = useCallback((notes) => {
        if (!notes) return '-';
        if (notes.length <= 90) return notes;
        return `${notes.slice(0, 90)}...`;
    }, []);

    const search = useCallback((showOverlay = true) => {
        let filters = [];
        if (!startDate) {
            warnAlert(t('validation.required.start_date'));
            return;
        }
        if (!endDate) {
            warnAlert('validation.required.end_date');
            return;
        }

        filters.push({
            field: "scheduledDate",
            value: formatDateToYYYYMMDD(startDate) + "T00:00:00",
            operation: SearchOperation.GREATER_THAN_EQUAL
        });
        filters.push({
            field: "scheduledDate",
            value: formatDateToYYYYMMDD(endDate) + "T23:59:59",
            operation: SearchOperation.LESS_THAN_EQUAL
        });

        // Adiciona a filial. 
        // Deve trazer apenas os pedidos da filial selecionada.
        // Se há contexto, usa ele. Senão, usa branchFilter (se selecionado)
        const branchToFilter = selectedBranch !== null ? selectedBranch : branchFilter;
        
        if (branchToFilter !== null) {
            filters.push({
                field: "branch.id",
                value: branchToFilter.value || branchToFilter.id,
                operation: SearchOperation.EQUAL
            });
        }

        if (customer) {
            filters.push({
                field: "customer.fullName",
                value: customer,
                operation: SearchOperation.MATCH
            });
        }

        if (employee) {
            filters.push({
                field: "employee.fullName",
                value: employee,
                operation: SearchOperation.MATCH
            });
        }

        if (type) {
            filters.push({
                field: "appointmentStatus",
                operation: SearchOperation.EQUAL,
                value: type.value
            });
        }

        let payload = {
            filters: filters,
            page: page,
            size: size,
            orders: [{
                field: "id",
                direction: "DESC"
            }]
        }

        if (showOverlay) {
            setShowLoading(true);
        }
        axiosInstance.post(Api.appointment.appointment + "/pageable", payload)
            .then((response) => {
                setPageable(response.data);
                setRows(response.data.content);
                // Uma chamada para a página inteira. Falhar aqui só esconde o ícone, não a lista.
                const ids = response.data.content.map((row) => row.id);
                if (ids.length === 0) {
                    setDepositSituations({});
                } else {
                    axiosInstance.post(Api.appointment.deposit + "/situations", ids)
                        .then((resp) => setDepositSituations(resp.data || {}))
                        .catch(() => setDepositSituations({}));
                }
            })
            .catch(error => errorAlert(error))
            .finally(() => {
                if (showOverlay) {
                    setShowLoading(false);
                }
            });
    }, [startDate, endDate, selectedBranch, branchFilter, customer, employee, type, page, size, t]);

    const redirectToNew = useCallback(() => {
        navigate(MenuConfig.appointment.appointment.route + "/new");
    }, [navigate]);

    // Altera a situação do agendamento
    const updateStatus = useCallback(() => {
        if (selectedRows.length === 0) {
            warnAlert(t('validation.required.row'));
            return;
        }

        // Exibe o modal
        setShowUpdateStatusModal(true);
    }, [selectedRows, t]);

    // Atualiza o status dos agendamentos selecionados em lote
    const handleUpdateAppointmentStatus = (selectedIds, newStatus, cancellationReason = null) => {
        const payload = {
            ids: selectedIds,
            status: newStatus,
            ...(cancellationReason && { reason: cancellationReason })
        };

        return axiosInstance.patch(Api.appointment.bulkUpdateStatus, payload)
            .then((response) => {
                const result = response.data;
                
                // Mostra resultado da operação
                if (result.failures > 0) {
                    // Algumas falhas ocorreram
                    warnAlert(
                        t('bulk_update_partial_success', {
                            successful: result.successfulUpdates,
                            total: result.totalRequested,
                            failures: result.failures
                        })
                    );
                } else {
                    // Todas as atualizações foram bem-sucedidas
                    successAlert(
                        t('bulk_update_success', {
                            count: result.successfulUpdates
                        })
                    );
                }

                // Log dos erros se houver
                if (result.errorMessages && result.errorMessages.length > 0) {
                    console.error('Bulk update errors:', result.errorMessages);
                }

                setShowUpdateStatusModal(false);
                setSelectedRows([]); // Limpa a seleção
                search(false); // Recarrega a lista sem overlay maior atrás do modal
            })
            .catch(error => {
                ApiError(error);
            });
    }

    const handleCancelUpdateStatus = () => {
        setShowUpdateStatusModal(false);
    }

    // Handler para atalhos de data
    const handleQuickDateSelect = useCallback((startDate, endDate) => {
        setStartDate(startDate);
        setEndDate(endDate);
    }, []);

    // Handler para limpar filtros
    const handleClearFilters = useCallback(() => {
        setStartDate(new Date());
        setEndDate(new Date());
        setCustomer('');
        setEmployee('');
        setType(null);
        // Preserva a filial do contexto, se houver
        if (selectedBranch !== null) {
            setBranchFilter({
                value: selectedBranch.id,
                label: selectedBranch.name
            });
        } else {
            setBranchFilter(null);
        }
    }, [selectedBranch]);

    // Handler para busca
    const handleSearch = useCallback(() => {
        setPage(0);
        search();
    }, [search]);

    // Sincroniza o filtro de filial com o contexto quando ele muda
    useEffect(() => {
        if (selectedBranch !== null) {
            setBranchFilter({
                value: selectedBranch.id,
                label: selectedBranch.name
            });
        } else {
            setBranchFilter(null);
        }
    }, [selectedBranch]);

    useEffect(() => {
        search();
    }, [page, size, selectedBranch]);

    return (
        <>
            <ModalPage
                title={t('update_status')}
                show={showUpdateStatusModal}
                setShow={setShowUpdateStatusModal}
                dialogClassName="appointment-status-dialog"
                child={
                    <AppointmentUpdateStatus
                        selectedRows={selectedRows}
                        onUpdateStatus={handleUpdateAppointmentStatus}
                        onCancel={handleCancelUpdateStatus}
                    />
                }
                size="md"
                centered={true}
                btnCloseOnClick={handleCancelUpdateStatus}
                btnCloseText={t('cancel')}
            />

            <AppointmentDetailsModal
                show={Boolean(selectedAppointment)}
                appointment={selectedAppointment}
                onHide={() => setSelectedAppointment(null)}
                onAppointmentUpdated={() => search()}
            />

            <FilterAccordion
                onSearch={handleSearch}
                onClear={handleClearFilters}
                showQuickDates={true}
                onQuickDateSelect={handleQuickDateSelect}
            >
                <Row className="g-3">
                    <Col xs={12} sm={6} lg={3}>
                        <FormLabel>{t('start_date')}</FormLabel>
                        <InputDate
                            onChange={setStartDate}
                            selected={startDate}
                        />
                    </Col>
                    <Col xs={12} sm={6} lg={3}>
                        <FormLabel>{t('end_date')}</FormLabel>
                        <InputDate
                            onChange={setEndDate}
                            selected={endDate}
                        />
                    </Col>
                    <Col xs={12} sm={6} lg={3}>
                        <FormLabel>{t('branch')}</FormLabel>
                        <BranchSelect
                            value={branchFilter}
                            onChange={(value) => {
                                setBranchFilter(value);
                            }}
                            isDisabled={selectedBranch !== null}
                            isClearable={true}
                        />
                    </Col>
                    <Col xs={12} sm={6} lg={3}>
                        <FormLabel>{t('status')}</FormLabel>
                        <AppointmentStatusSelect
                            value={type}
                            onChange={setType}
                            isClearable={true}
                        />
                    </Col>
                </Row>
                <Row className="g-3 mt-0">
                    <Col xs={12} md={6}>
                        <FormLabel>{t('customer')}</FormLabel>
                        <InputText
                            value={customer}
                            onChange={(e) => setCustomer(e.target.value)}
                        />
                    </Col>
                    <Col xs={12} md={6}>
                        <FormLabel>{t('employee')}</FormLabel>
                        <InputText
                            value={employee}
                            onChange={(e) => setEmployee(e.target.value)}
                        />
                    </Col>
                </Row>
            </FilterAccordion>

            <Row className="row mb-3">
                <Col md={12}>
                    <ButtonGroup>
                        <Dropdown>
                            <Dropdown.Toggle className="me-2" variant="primary">
                                {t('actions')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => redirectToNew()}><SaveIcon /> {t('new')}</Dropdown.Item>
                                <Dropdown.Item onClick={() => updateStatus()}><UpdateStatusIcon /> {t('update_status')}</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </ButtonGroup>
                </Col>
            </Row>
            <GridCustomBase
                pageable={pageable}
                setPage={setPage}
                setSize={setSize}
                showLoading={showLoading}
                selectedCount={selectedRows.length}>

                <table className="table table-list i-table style-1 mb-0 border-0 appointment-history-table">
                    <thead>
                        <tr>
                            <th></th>
                            <th style={{ width: 220 }}>{t("scheduled_date")}</th>
                            <th style={{ width: 400 }}>{t("customer")}</th>
                            <th style={{ width: 260 }}>Atendimento</th>
                            <th>{t("obs")}</th>
                            <th style={{ width: 170 }} className="text-center"><strong>{t("status")}</strong></th>
                            <th style={{ width: 72 }} className="text-center">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, index) => (
                            <tr key={index} className={
                                `align-middle appointment-history-table__row ${row.appointmentStatus === AppointmentStatus.CANCELED ? 'is-canceled' : ''}`}>
                                <GridCustomCheckBoxRow row={row} setSelectedRows={setSelectedRows} hiddenChecked={row.appointmentStatus !== AppointmentStatus.SCHEDULED} />
                                <td>
                                    <div className="appointment-history-table__stack">
                                        <strong className="appointment-history-table__primary">{formatDateTime(row.scheduledDate)}</strong>
                                        <span className="appointment-history-table__meta">
                                            <span>{row.duration ? `${row.duration} min` : '-'}</span>
                                            <span className="appointment-history-table__dot"></span>
                                            <span>{row.price ? `${t('currency_symbol')} ${formatNumberWithTwoDecimals(row.price)}` : '-'}</span>
                                            {depositSituations[row.id] && (
                                                <>
                                                    <span className="appointment-history-table__dot"></span>
                                                    <AppointmentDepositBadge
                                                        situation={depositSituations[row.id].situation}
                                                        value={depositSituations[row.id].value} />
                                                </>
                                            )}
                                        </span>
                                    </div>
                                </td>
                                <td>
                                    <div className="appointment-history-table__stack">
                                        <div className="appointment-history-table__primary-wrap">
                                            <strong className="appointment-history-table__primary">
                                                {getCustomerName(row)}
                                                {row.customer?.clubMember && (
                                                    <ClubCrownIcon />
                                                )}
                                                {row.customer?.clubLate && (
                                                    <ClubLateIcon />
                                                )}
                                            </strong>
                                            {row.customer?.phone ? (
                                                <Whatsapp
                                                    phone={row.customer.phone}
                                                    className="appointment-history-table__whatsapp"
                                                />
                                            ) : null}
                                        </div>
                                        <span className="appointment-history-table__meta">{row.customer?.phone || '-'}</span>
                                        {row.customer?.branch?.name && (
                                            <span className="appointment-history-table__meta mt-1" title={`Unidade de origem: ${row.customer.branch.name}`}>
                                                <i className="fa-solid fa-location-dot text-muted me-1"></i>
                                                Origem: {row.customer.branch.name}
                                            </span>
                                        )}
                                        {row.customer?.clubPlanName && (
                                            <span className="appointment-history-table__meta mt-1 text-warning fw-bold" title={`Plano do Clube: ${row.customer.clubPlanName}`}>
                                                <i className="fa-solid fa-gem me-1"></i>
                                                Plano: {row.customer.clubPlanName}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="appointment-history-table__employee">
                                        <div className="appointment-history-table__stack">
                                            <strong className="appointment-history-table__primary">{getEmployeeName(row)}</strong>
                                            <span className="appointment-history-table__meta">{getServiceName(row)}</span>
                                            <span className="appointment-history-table__subtle">{getBranchName(row)}</span>
                                        </div>
                                    </div>
                                </td>
                                <td title={row.notes || ''}>
                                    <span className={`appointment-history-table__notes ${!row.notes ? 'is-empty' : ''}`}>
                                        {getTrimmedNotes(row.notes)}
                                    </span>
                                </td>
                                <td className="text-center">
                                    <AppointmentCurrentStatus row={row} />
                                </td>
                                <td className="text-center">
                                    <button
                                        type="button"
                                        className="btn btn-link appointment-history-table__detail-button"
                                        onClick={() => setSelectedAppointment(row)}
                                        title="Detalhes"
                                        aria-label="Detalhes"
                                    >
                                        <i className="fas fa-eye"></i>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </GridCustomBase>
        </>
    );
}

export default AppointmentHistory;
